import React, { forwardRef, useEffect, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Loader } from '@googlemaps/js-api-loader';
import { useDebouncedCallback } from 'use-debounce';

import { SelectOption, ZZMForm } from '@zazume/zzm-base';
import { containsNumbers, isNotEmpty } from '@zazume/zzm-utils';

import { useI18n } from '#/hooks/useI18n';

import autocompletionRequestBuilder from './autocompletionRequestBuilder';
import { AutocompletionRequest, GooglePlacesAutocompleteProps } from './GooglePlacesAutocomplete.types';

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const Suggestions = styled.div(
  ({ theme }) => css`
    position: absolute;
    max-width: 320px;
    max-height: 248px;
    background-color: ${theme.colors.White};
    background-color: ${theme.colors.White};
    min-width: 160px;
    box-shadow: ${theme.shadows.s10};
    z-index: 1;
    padding: 0;
    margin: 0;
    overflow-y: auto;
    border-radius: 8px;
  `
);

/**
 * Inspired by https://github.com/Tintef/react-google-places-autocomplete
 */

const ZZMGooglePlacesAutocomplete: React.ForwardRefRenderFunction<any, GooglePlacesAutocompleteProps> = (
  {
    apiKey = '',
    autocompletionRequest = {},
    debounce = 300,
    minLengthAutocomplete = 0,
    selectProps = {},
    withSessionToken = false,
    onChangeFullAddress,
    onChangeValue,
    addressWithoutNumber = false,
    isOptional = false,
    placeIdName,
    responsive = false,
    label,
    disabled,
    ...rest
  }: GooglePlacesAutocompleteProps,
  ref
): React.ReactElement => {
  const { t } = useI18n();
  // @ts-ignore
  const [placesService, setPlacesService] = useState<google.maps.places.AutocompleteService | undefined>(undefined);
  // @ts-ignore
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [selection, setSelection] = useState<null | any>({});

  const { name = 'fullAddress' } = rest;

  const { setError, clearErrors, setValue } = ZZMForm.useFormContext();

  const fetchSuggestions = useDebouncedCallback((value: string, cb: (options: any) => void): void => {
    if (!placesService) {
      return cb([]);
    }
    if (value.length < minLengthAutocomplete) {
      return cb([]);
    }

    const autocompletionReq: AutocompletionRequest = { ...autocompletionRequest };

    placesService.getPlacePredictions(
      autocompletionRequestBuilder(autocompletionReq, value, withSessionToken && sessionToken),
      suggestions => {
        cb((suggestions || []).map(suggestion => ({ label: suggestion.description, value: suggestion })));
      }
    );
  }, debounce);

  const initializeService = () => {
    // @ts-ignore
    if (!window.google) {
      console.error('Google script not loaded');
      return;
    }
    // @ts-ignore
    if (!window.google.maps) {
      console.error('Google maps script not loaded');
      return;
    }
    // @ts-ignore
    if (!window.google.maps.places) {
      console.error('Google maps places script not loaded');
      return;
    }

    // @ts-ignore
    setPlacesService(new window.google.maps.places.AutocompleteService());
    // @ts-ignore
    setSessionToken(new google.maps.places.AutocompleteSessionToken());
  };

  useEffect(() => {
    const init = async () => {
      try {
        await new Loader({ apiKey, ...{ libraries: ['places'] } }).load();
        initializeService();
      } catch (error) {
        console.error(error);
      }
    };

    if (apiKey) {
      init();
    } else {
      initializeService();
    }
  }, [apiKey]);

  const validateInput = (value: string) => {
    if (value !== selection?.value?.description) {
      setError(name, { type: 'required', message: t('general.forms.mandatoryGooglePlacesSelection') });
    }

    if (!addressWithoutNumber && !containsNumbers(value)) {
      setError(name, { type: 'required', message: t('general.forms.addressNumberMandatory') });
    }

    if (isOptional && !isNotEmpty(value)) {
      clearErrors(name);
    }
  };

  const inputHandler = (value: string) => {
    validateInput(value);
    onChangeValue?.(value);

    fetchSuggestions(value, options => {
      setSuggestions(options);
      if (!options.length) {
        setError(name, {
          type: 'required',
          message: t('general.forms.invalidGooglePlacesSelection')
        });
      }
    });
  };

  const handleOnClickOption = option => () => {
    setSuggestions([]);
    setSelection(option);
    if (!addressWithoutNumber && !containsNumbers(option.value.description)) {
      setError(name, { type: 'required', message: t('general.forms.addressNumberMandatory') });
    } else {
      clearErrors && clearErrors(name);
    }
    const VALID_ADDRESS_TYPES = ['street_address', 'route', 'subpremise', 'premise'];
    const isStreetAddress = option.value.types.some(type => VALID_ADDRESS_TYPES.includes(type));
    onChangeFullAddress && onChangeFullAddress(option.label, isStreetAddress);
    setValue(name, option.label, { shouldDirty: true });
    placeIdName && setValue(placeIdName, option.value?.place_id, { shouldDirty: true });
  };

  return (
    <Container>
      {placeIdName && <input type="hidden" name={placeIdName} ref={ref} />}
      <ZZMForm.Input
        name={name}
        type="text"
        label={label}
        placeholder={t('general.forms.typeAndSelect')}
        onChangeValue={inputHandler}
        autoComplete="off"
        onBlurValue={validateInput}
        responsive={responsive}
        disabled={disabled}
      />
      <Suggestions>
        {suggestions.map(suggestion => (
          <SelectOption
            key={suggestion.value.place_id}
            value={suggestion.value}
            name={suggestion.label}
            onClick={handleOnClickOption(suggestion)}
          >
            {suggestion.label}
          </SelectOption>
        ))}
      </Suggestions>
    </Container>
  );
};

export default forwardRef(ZZMGooglePlacesAutocomplete);
